import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/ui/Button'
import ThemeToggle from '../components/ui/ThemeToggle'

const API_URL = import.meta.env.VITE_API_URL || ''

const Pricing = () => {
  const navigate = useNavigate()
  const { login, loading, user, isAuthenticated } = useAuth()
  const [annual, setAnnual] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showFullTerms, setShowFullTerms] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(null)

  // Check if user is a subscriber (has any paid plan)
  const isSubscriber = user?.plan && user.plan !== 'free'

  const plans = [
    { 
      name: 'Hobby', 
      key: 'hobby',
      monthly: 19.99, 
      annual: 199, 
      gens: 50, 
      features: [
        'Zenna AI chat assistant',
        'AI image generation (50/mo)',
        'PNG, SVG, DXF exports',
        'PES, DST, JEF, EXP exports',
        '2,500+ Google fonts',
        'Text tool with 5 layouts',
        'Full Color Tools access',
        'Thread & vinyl matching',
        '3 project folders',
        'Basic email support'
      ] 
    },
    { 
      name: 'Crafter', 
      key: 'crafter',
      monthly: 39.99, 
      annual: 399, 
      gens: 150, 
      features: [
        'Everything in Hobby, plus:',
        'AI image generation (150/mo)',
        'Priority generation queue',
        'Save custom presets',
        'Unlimited project folders',
        'Save 25 color palettes',
        'Design complexity analysis',
        'Material cost estimator',
        'Email support (24hr response)'
      ],
      popular: true 
    },
    { 
      name: 'Creator', 
      key: 'creator',
      monthly: 69.99, 
      annual: 699, 
      gens: 400, 
      features: [
        'Everything in Crafter, plus:',
        'AI image generation (400/mo)',
        '50,000+ premium fonts',
        'Commercial use license',
        'Batch exports',
        'Save 50 color palettes',
        'Machine-specific presets',
        'Priority email support'
      ] 
    },
    { 
      name: 'Pro', 
      key: 'pro',
      monthly: 129.99, 
      annual: 1299, 
      gens: 'Unlimited', 
      features: [
        'Everything in Creator, plus:',
        'Unlimited AI generations',
        'API access',
        'Custom integrations',
        'White-label exports',
        'Early access to features',
        'Dedicated support'
      ] 
    },
  ]

  const creditPacks = [
    { credits: 10, key: 'credits_10', price: 4.99, perCredit: 0.50, savings: null, bonus: 1 },
    { credits: 25, key: 'credits_25', price: 9.99, perCredit: 0.40, savings: 20, bonus: 3 },
    { credits: 50, key: 'credits_50', price: 17.99, perCredit: 0.36, savings: 28, bonus: 5 },
    { credits: 100, key: 'credits_100', price: 29.99, perCredit: 0.30, savings: 40, bonus: 15 },
  ]

  // Handle plan purchase — redirects to Stripe Checkout
  const handlePlanSelect = async (plan) => {
    if (!isAuthenticated) {
      // Not logged in — trigger login, user will need to click again after
      login()
      return
    }

    const priceKey = plan.key + (annual ? '_annual' : '_monthly')
    setCheckoutLoading(plan.key)

    try {
      const res = await fetch(`${API_URL}/billing/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          priceKey
        })
      })

      const data = await res.json()

      if (data.url) {
        window.location.href = data.url
      } else {
        alert(data.error || 'Failed to start checkout')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      alert('Something went wrong. Please try again.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  // Handle credit pack purchase
  const handleCreditPack = async (pack) => {
    if (!isAuthenticated) {
      login()
      return
    }

    setCheckoutLoading(pack.key)

    try {
      const res = await fetch(`${API_URL}/billing/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          priceKey: pack.key
        })
      })

      const data = await res.json()

      if (data.url) {
        window.location.href = data.url
      } else {
        alert(data.error || 'Failed to start checkout')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      alert('Something went wrong. Please try again.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-zenna-cream dark:bg-slate-900">
      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white">
                Credit Pack Terms
              </h3>
              <button 
                onClick={() => setShowTerms(false)} 
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Credits Never Expire</h4>
                <p>Your purchased credits remain in your account indefinitely. Use them whenever you're ready - no rush!</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Credit Packs: Non-Refundable</h4>
                <p>All credit pack purchases are final. Credits cannot be refunded or exchanged for cash.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Subscription Cancellation: Prorated Refund</h4>
                <p>When you cancel a subscription, you'll receive a prorated refund for unused generations:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  <li>Generations used are calculated at the credit pack rate ($0.40/credit)</li>
                  <li>Refund = Amount paid − (Generations used × $0.40)</li>
                  <li>Refunds are processed within 5-10 business days</li>
                </ul>
                <p className="mt-2 text-xs italic">Example: Cancel Crafter ($39.99) after using 50 generations = $39.99 − $20.00 = $19.99 refund</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Non-Transferable</h4>
                <p>Credits are tied to your account and cannot be transferred to another user.</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Account Suspension</h4>
                <p>If your account is suspended for policy violations, your credits are frozen (not lost) pending the appeal process:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                  <li><span className="text-emerald-600 dark:text-emerald-400">Appeal successful:</span> Account restored with all credits intact</li>
                  <li><span className="text-red-600 dark:text-red-400">Appeal denied:</span> Credits are donated to our Craft for Good program (supporting schools, nonprofits, and therapy craft programs), and account is permanently closed</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Subscriber Bonuses</h4>
                <p>Active subscribers on any paid plan receive bonus credits with every pack purchase. Bonuses are applied automatically at checkout.</p>
              </div>
            </div>
            
            <Button className="w-full mt-6" onClick={() => setShowTerms(false)}>
              Got It
            </Button>
          </div>
        </div>
      )}

      {/* Full Terms & Conditions Modal */}
      {showFullTerms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white">
                Terms & Conditions
              </h3>
              <button 
                onClick={() => setShowFullTerms(false)} 
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-xs text-slate-400 mb-6">Last updated: February 8, 2026</p>
            
            <div className="space-y-6 text-sm text-slate-600 dark:text-slate-400">
              
              {/* Service Overview */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">1. Service Overview</h4>
                <p>Zenna ("the Service") is an AI-powered craft companion operated by ZenFlow Studios LLC ("we", "us", "our"). By using Zenna, you agree to these Terms & Conditions.</p>
              </div>

              {/* Account Terms */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">2. Account Terms</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>You must be 18 years or older to create an account</li>
                  <li>You are responsible for maintaining the security of your account</li>
                  <li>One account per person; multiple accounts may be terminated</li>
                  <li>You must provide accurate information during registration</li>
                </ul>
              </div>

              {/* Subscription Terms */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">3. Subscription Plans</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li><strong>Free Trial:</strong> All paid plans include a 7-day free trial. Cancel anytime during the trial and only pay for credits used (at $0.40/credit rate).</li>
                  <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually based on your selection.</li>
                  <li><strong>Generation Credits:</strong> Plan credits are added to your balance on each billing cycle and roll over indefinitely.</li>
                  <li><strong>Plan Changes:</strong> You may upgrade or downgrade at any time. Changes take effect on your next billing cycle.</li>
                  <li><strong>Price Changes:</strong> We may adjust pricing with 30 days notice. Annual subscribers are locked in at their rate for 12 months.</li>
                </ul>
              </div>

              {/* Cancellation & Refunds */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">4. Cancellation & Refunds</h4>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-3">
                  <p className="font-medium text-slate-900 dark:text-white mb-2">Prorated Refund Policy</p>
                  <p className="mb-2">When you cancel a subscription, you receive a prorated refund calculated as:</p>
                  <p className="font-mono text-xs bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-600">
                    Refund = Amount Paid − (Generations Used × $0.40)
                  </p>
                  <p className="text-xs mt-2 italic">Example: Cancel Crafter ($39.99) after using 50 generations = $39.99 − $20.00 = <span className="text-emerald-600 dark:text-emerald-400 font-medium">$19.99 refund</span></p>
                </div>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>Refunds are processed within 5-10 business days</li>
                  <li>Annual plans are refunded proportionally for remaining months</li>
                  <li>You retain access until the end of your current billing period</li>
                </ul>
              </div>

              {/* Credit Packs */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">5. Credit Packs</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li><strong>Non-Refundable:</strong> All credit pack purchases are final</li>
                  <li><strong>Never Expire:</strong> Purchased credits remain in your account indefinitely</li>
                  <li><strong>Non-Transferable:</strong> Credits cannot be transferred between accounts</li>
                  <li><strong>Subscriber Bonuses:</strong> Active subscribers receive bonus credits with pack purchases</li>
                </ul>
              </div>

              {/* Content & Usage */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">6. Content & Usage Rights</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li><strong>Your Content:</strong> You retain ownership of designs you create using Zenna</li>
                  <li><strong>Commercial Use:</strong> Creator and Pro plans include commercial use rights</li>
                  <li><strong>Hobby/Crafter Plans:</strong> Personal use only; commercial use requires upgrade</li>
                  <li><strong>AI Generations:</strong> Images generated by AI are yours to use per your plan terms</li>
                </ul>
              </div>

              {/* Prohibited Content */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">7. Prohibited Content</h4>
                <p className="mb-2">You may not use Zenna to create:</p>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>Content that sexualizes or exploits minors</li>
                  <li>Non-consensual intimate imagery</li>
                  <li>Content promoting violence, terrorism, or hate</li>
                  <li>Content infringing on intellectual property rights</li>
                  <li>Deceptive content (deepfakes, misinformation)</li>
                  <li>Content violating any applicable laws</li>
                </ul>
                <p className="mt-2 text-xs">We actively monitor for prohibited content and report illegal material to appropriate authorities including the National Center for Missing & Exploited Children (NCMEC).</p>
              </div>

              {/* Account Suspension */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">8. Account Suspension & Termination</h4>
                <p className="mb-2">If your account is suspended for policy violations:</p>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>Your credits are <strong>frozen</strong> (not lost) pending appeal</li>
                  <li>You have 30 days to submit an appeal</li>
                  <li><span className="text-emerald-600 dark:text-emerald-400">Appeal successful:</span> Account restored with all credits intact</li>
                  <li><span className="text-red-600 dark:text-red-400">Appeal denied:</span> Credits are donated to our <strong>Craft for Good</strong> program (supporting schools, nonprofits, and therapy craft programs)</li>
                </ul>
              </div>

              {/* Privacy */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">9. Privacy & Data</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>We collect only information necessary to provide the Service</li>
                  <li>Your designs are private and not shared publicly</li>
                  <li>We do not sell your personal information</li>
                  <li>You may request deletion of your data at any time</li>
                  <li>See our Privacy Policy for complete details</li>
                </ul>
              </div>

              {/* Disclaimers */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">10. Disclaimers</h4>
                <ul className="list-disc list-inside space-y-1.5 ml-2">
                  <li>The Service is provided "as is" without warranties</li>
                  <li>AI-generated content may not always meet expectations</li>
                  <li>We are not liable for how you use generated designs</li>
                  <li>Machine settings and material recommendations are suggestions only</li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2 text-base">11. Contact Us</h4>
                <p>For questions about these terms, contact us at:</p>
                <p className="mt-2">
                  <strong>Email:</strong> <a href="mailto:legal@zennago.com" className="text-zenna-violet hover:underline">legal@zennago.com</a><br/>
                  <strong>Support:</strong> <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">support@zennago.com</a>
                </p>
              </div>

              {/* Governing Law */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-400">
                  These Terms are governed by the laws of the State of North Carolina, United States. 
                  By using Zenna, you agree to these Terms & Conditions.
                </p>
              </div>
            </div>
            
            <Button className="w-full mt-6" onClick={() => setShowFullTerms(false)}>
              I Understand
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 sm:gap-3"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-zenna flex items-center justify-center shadow-lg shadow-zenna-violet/20">
              <span className="text-white font-bold text-lg sm:text-xl">Z</span>
            </div>
            <span className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white">
              Zenna
            </span>
          </button>

          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            {isAuthenticated ? (
              <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => login()} loading={loading}>
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3 sm:mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6 sm:mb-8 px-4">
            Lock in your rate with annual billing. All plans include a 7-day free trial.
          </p>
          
          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 sm:gap-2 bg-white dark:bg-slate-800 p-1 sm:p-1.5 rounded-full border border-slate-200 dark:border-slate-700">
            <button 
              onClick={() => setAnnual(false)} 
              className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                !annual 
                  ? 'bg-gradient-zenna text-white shadow' 
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button 
              onClick={() => setAnnual(true)} 
              className={`px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                annual 
                  ? 'bg-gradient-zenna text-white shadow' 
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Annual <span className="text-emerald-400 ml-1">-17%</span>
            </button>
          </div>
        </div>

        {/* Plans - Horizontal scroll on mobile */}
        <div className="mb-8 sm:mb-12 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 overflow-x-auto pb-4 sm:pb-0 snap-x snap-mandatory sm:snap-none">
            {plans.map((plan) => (
              <div 
                key={plan.name}
                className={`relative flex-shrink-0 w-[280px] sm:w-auto bg-white dark:bg-slate-800 rounded-2xl border-2 p-5 sm:p-6 transition-all snap-center ${
                  plan.popular 
                    ? 'border-zenna-violet shadow-xl shadow-zenna-violet/10' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-zenna-violet/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 bg-gradient-zenna text-white text-[10px] sm:text-xs font-bold rounded-full shadow-lg whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                
                <div className="mb-3 sm:mb-4">
                  <span className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
                    ${annual ? Math.round(plan.annual / 12) : plan.monthly}
                  </span>
                  <span className="text-slate-500 text-sm">/mo</span>
                  {annual && (
                    <p className="text-xs text-slate-400 mt-1">
                      Billed ${plan.annual}/year
                    </p>
                  )}
                </div>

                <p className="text-xs sm:text-sm text-zenna-violet dark:text-zenna-cyan font-medium mb-3 sm:mb-4">
                  {plan.gens === 'Unlimited' ? 'Unlimited' : plan.gens} generations/mo
                </p>

                <ul className="space-y-2 sm:space-y-2.5 mb-5 sm:mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                      {feature.includes('Everything in') ? (
                        <>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-zenna-violet flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                          <span className="text-zenna-violet dark:text-zenna-cyan font-medium">{feature}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-slate-600 dark:text-slate-400">{feature}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>

                <Button 
                  variant={plan.popular ? 'primary' : 'secondary'}
                  className="w-full"
                  size="sm"
                  onClick={() => handlePlanSelect(plan)}
                  loading={checkoutLoading === plan.key}
                >
                  {isAuthenticated ? (isSubscriber ? 'Change Plan' : 'Get Started') : 'Sign In to Subscribe'}
                </Button>

                {annual && (
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-2 sm:mt-3 text-center flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Rate locked for 12 months
                  </p>
                )}
              </div>
            ))}
          </div>
          {/* Scroll hint for mobile */}
          <p className="sm:hidden text-center text-xs text-slate-400 mt-2">
            ← Swipe to see all plans →
          </p>
        </div>

        {/* Credit Packs */}
        <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-700 p-5 sm:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-5 sm:mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white">
                Need more generations?
              </h2>
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
                Buy credit packs anytime - they never expire!
              </p>
            </div>
            {isSubscriber && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-xs font-medium">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Subscriber bonus active!
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {creditPacks.map((pack) => (
              <button
                key={pack.credits}
                onClick={() => handleCreditPack(pack)}
                disabled={checkoutLoading === pack.key}
                className="relative p-4 sm:p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-zenna-violet hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all text-center group disabled:opacity-50"
              >
                {/* Savings badge */}
                {pack.savings && (
                  <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-emerald-500 text-white text-[10px] sm:text-xs font-bold rounded-full">
                    {pack.savings}% OFF
                  </div>
                )}
                
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-1">
                  {pack.credits}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">credits</p>
                
                {/* Subscriber bonus */}
                {pack.bonus > 0 && (
                  <p className="text-[10px] sm:text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-2">
                    +{pack.bonus} free for subscribers
                  </p>
                )}
                
                <p className="text-lg sm:text-xl text-zenna-violet dark:text-zenna-cyan font-bold">
                  ${pack.price}
                </p>
                <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                  ${pack.perCredit.toFixed(2)}/credit
                </p>

                {checkoutLoading === pack.key && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-800/80 rounded-xl">
                    <div className="w-5 h-5 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Terms link */}
          <p className="text-xs text-slate-400 text-center mt-4">
            Credits are non-refundable and non-transferable.{' '}
            <button 
              onClick={() => setShowTerms(true)}
              className="text-zenna-violet hover:underline"
            >
              View full terms
            </button>
          </p>
        </div>

        {/* Upgrade promo */}
        {!annual && (
          <div className="bg-gradient-to-r from-zenna-violet/10 to-zenna-emerald/10 dark:from-zenna-violet/20 dark:to-zenna-emerald/20 rounded-xl sm:rounded-2xl border border-zenna-violet/20 p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-1">
                  Already on monthly?
                </h2>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
                  Switch to annual and we'll apply what you've paid toward the full year price!
                </p>
              </div>
              <Button onClick={() => setAnnual(true)} className="w-full sm:w-auto">
                See Your Savings →
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center text-xs sm:text-sm text-slate-500 dark:text-slate-400">
          <p>All plans include a 7-day free trial. Cancel anytime.</p>
          <p className="mt-2">Questions? <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">Contact us</a></p>
          <p className="mt-3 flex items-center justify-center gap-3">
            <button 
              onClick={() => setShowFullTerms(true)}
              className="text-zenna-violet hover:underline"
            >
              Terms & Conditions
            </button>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <a href="/privacy" className="text-zenna-violet hover:underline">
              Privacy Policy
            </a>
          </p>
          <p className="mt-4 text-xs text-slate-400">
            © 2026 ZenFlow Studios LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default Pricing